export const MAX_FILE_MB = 100
export const MAX_COMPARE_PAGES = 200

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const TOO_LITTLE_TEXT_CHARS = 40
const LINE_Y_TOLERANCE = 2
const MAX_LCS_CELLS = 120_000
const SAMPLE_LINE_LIMIT = 8

export interface CompareProgress {
  stage: 'left' | 'right' | 'compare'
  current: number
  total: number
}

export interface ExtractedComparePdf {
  fileName: string
  pageCount: number
  charCount: number
  wordCount: number
  pages: PageText[]
  warning: 'too_little_text' | null
}

export interface PageText {
  pageNumber: number
  text: string
}

export interface CompareSummary {
  status: 'identical' | 'different'
  leftPages: number
  rightPages: number
  commonPages: number
  equalPages: number
  changedPages: number
  addedPages: number
  removedPages: number
  leftWords: number
  rightWords: number
  leftChars: number
  rightChars: number
  similarityPercent: number
}

export interface PageDiff {
  pageNumber: number
  kind: 'changed' | 'added' | 'removed'
  removedLines: number
  addedLines: number
  removedSample: string[]
  addedSample: string[]
  truncated: boolean
}

export interface CompareResult {
  left: ExtractedComparePdf
  right: ExtractedComparePdf
  summary: CompareSummary
  diffs: PageDiff[]
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfJsPage>
  destroy: () => Promise<void>
}

interface PdfJsPage {
  getTextContent: () => Promise<PdfTextContent>
  cleanup: () => void
}

interface PdfTextContent {
  items: PdfTextItem[]
}

interface PdfTextItem {
  str: string
  transform: number[]
}

interface TextLine {
  y: number
  items: PdfTextItem[]
}

type DiffLine = { kind: 'same' | 'removed' | 'added'; text: string }

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

function itemX(item: PdfTextItem): number {
  return item.transform[4] ?? 0
}

function itemY(item: PdfTextItem): number {
  return item.transform[5] ?? 0
}

function textItemValue(item: PdfTextItem): string {
  return item.str.replace(/\s+/g, ' ').trim()
}

function pageText(items: PdfTextItem[]): string {
  const lines: TextLine[] = []
  for (const item of items) {
    const value = textItemValue(item)
    if (!value) continue

    const y = itemY(item)
    const line = lines.find((candidate) => Math.abs(candidate.y - y) <= LINE_Y_TOLERANCE)
    if (line) {
      line.items.push(item)
    } else {
      lines.push({ y, items: [item] })
    }
  }

  return lines
    .sort((a, b) => b.y - a.y)
    .map((line) =>
      line.items
        .sort((a, b) => itemX(a) - itemX(b))
        .map(textItemValue)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean)
    .join('\n')
}

function normaliseText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function normaliseLines(text: string): string[] {
  return text
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}

function sample(lines: string[]): string[] {
  return lines.slice(0, SAMPLE_LINE_LIMIT)
}

function fallbackLineDiff(leftLines: string[], rightLines: string[]): DiffLine[] {
  const max = Math.max(leftLines.length, rightLines.length)
  const result: DiffLine[] = []

  for (let index = 0; index < max; index++) {
    const left = leftLines[index]
    const right = rightLines[index]
    if (left === right && left) {
      result.push({ kind: 'same', text: left })
    } else {
      if (left) result.push({ kind: 'removed', text: left })
      if (right) result.push({ kind: 'added', text: right })
    }
  }

  return result
}

function lineDiff(leftLines: string[], rightLines: string[]): DiffLine[] {
  if (leftLines.length * rightLines.length > MAX_LCS_CELLS) {
    return fallbackLineDiff(leftLines, rightLines)
  }

  const rows = leftLines.length + 1
  const cols = rightLines.length + 1
  const dp = Array.from({ length: rows }, () => Array<number>(cols).fill(0))

  for (let i = leftLines.length - 1; i >= 0; i--) {
    for (let j = rightLines.length - 1; j >= 0; j--) {
      const row = dp[i]
      const nextRow = dp[i + 1]
      const left = leftLines[i]
      const right = rightLines[j]
      if (!row || !nextRow || left === undefined || right === undefined) continue

      row[j] =
        left === right
          ? (nextRow[j + 1] ?? 0) + 1
          : Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0)
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0

  while (i < leftLines.length && j < rightLines.length) {
    const left = leftLines[i]
    const right = rightLines[j]
    if (left === undefined || right === undefined) break

    if (left === right) {
      result.push({ kind: 'same', text: left })
      i++
      j++
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      result.push({ kind: 'removed', text: left })
      i++
    } else {
      result.push({ kind: 'added', text: right })
      j++
    }
  }

  while (i < leftLines.length) {
    const left = leftLines[i]
    if (left !== undefined) result.push({ kind: 'removed', text: left })
    i++
  }

  while (j < rightLines.length) {
    const right = rightLines[j]
    if (right !== undefined) result.push({ kind: 'added', text: right })
    j++
  }

  return result
}

function pageDiff(pageNumber: number, leftText: string, rightText: string): PageDiff {
  const diff = lineDiff(normaliseLines(leftText), normaliseLines(rightText))
  const removed = diff.filter((line) => line.kind === 'removed').map((line) => line.text)
  const added = diff.filter((line) => line.kind === 'added').map((line) => line.text)

  return {
    pageNumber,
    kind: 'changed',
    removedLines: removed.length,
    addedLines: added.length,
    removedSample: sample(removed),
    addedSample: sample(added),
    truncated: removed.length > SAMPLE_LINE_LIMIT || added.length > SAMPLE_LINE_LIMIT,
  }
}

function pageAdded(page: PageText): PageDiff {
  const lines = normaliseLines(page.text)
  return {
    pageNumber: page.pageNumber,
    kind: 'added',
    removedLines: 0,
    addedLines: lines.length,
    removedSample: [],
    addedSample: sample(lines),
    truncated: lines.length > SAMPLE_LINE_LIMIT,
  }
}

function pageRemoved(page: PageText): PageDiff {
  const lines = normaliseLines(page.text)
  return {
    pageNumber: page.pageNumber,
    kind: 'removed',
    removedLines: lines.length,
    addedLines: 0,
    removedSample: sample(lines),
    addedSample: [],
    truncated: lines.length > SAMPLE_LINE_LIMIT,
  }
}

export async function extractComparePdfText({
  file,
  signal,
  onProgress,
  stage,
}: {
  file: File
  signal: AbortSignal
  stage: CompareProgress['stage']
  onProgress: (progress: CompareProgress) => void
}): Promise<ExtractedComparePdf> {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const bytes = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: bytes }).promise

  try {
    if (doc.numPages > MAX_COMPARE_PAGES) {
      throw new Error('too_many_pages')
    }

    const pages: PageText[] = []
    onProgress({ stage, current: 0, total: doc.numPages })

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

      const page = await doc.getPage(pageNumber)
      const content = await page.getTextContent()
      const text = pageText(content.items)
      pages.push({ pageNumber, text })
      page.cleanup()

      onProgress({ stage, current: pageNumber, total: doc.numPages })
    }

    const text = pages.map((page) => page.text).join('\n\n').trim()
    const charCount = text.replace(/\s/g, '').length
    const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0
    const warning = charCount > 0 && charCount < TOO_LITTLE_TEXT_CHARS ? 'too_little_text' : null

    if (charCount === 0) {
      throw new Error('no_text_layer')
    }

    return {
      fileName: file.name,
      pageCount: doc.numPages,
      charCount,
      wordCount,
      pages,
      warning,
    }
  } finally {
    await doc.destroy().catch(() => {})
  }
}

export function comparePdfText(
  left: ExtractedComparePdf,
  right: ExtractedComparePdf,
): CompareResult {
  const commonPages = Math.min(left.pageCount, right.pageCount)
  let equalPages = 0
  const diffs: PageDiff[] = []

  for (let index = 0; index < commonPages; index++) {
    const leftPage = left.pages[index]
    const rightPage = right.pages[index]
    if (!leftPage || !rightPage) continue

    if (normaliseText(leftPage.text) === normaliseText(rightPage.text)) {
      equalPages++
    } else {
      diffs.push(pageDiff(index + 1, leftPage.text, rightPage.text))
    }
  }

  if (left.pageCount > right.pageCount) {
    for (let index = right.pageCount; index < left.pageCount; index++) {
      const page = left.pages[index]
      if (page) diffs.push(pageRemoved(page))
    }
  }

  if (right.pageCount > left.pageCount) {
    for (let index = left.pageCount; index < right.pageCount; index++) {
      const page = right.pages[index]
      if (page) diffs.push(pageAdded(page))
    }
  }

  const changedPages = diffs.filter((diff) => diff.kind === 'changed').length
  const addedPages = Math.max(0, right.pageCount - left.pageCount)
  const removedPages = Math.max(0, left.pageCount - right.pageCount)
  const totalComparablePages = Math.max(left.pageCount, right.pageCount, 1)
  const status = diffs.length === 0 ? 'identical' : 'different'

  return {
    left,
    right,
    diffs,
    summary: {
      status,
      leftPages: left.pageCount,
      rightPages: right.pageCount,
      commonPages,
      equalPages,
      changedPages,
      addedPages,
      removedPages,
      leftWords: left.wordCount,
      rightWords: right.wordCount,
      leftChars: left.charCount,
      rightChars: right.charCount,
      similarityPercent: Math.round((equalPages / totalComparablePages) * 100),
    },
  }
}
